import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp, Building2, Building, Mountain, DollarSign, Sparkles,
  ArrowUpRight, Activity, Send, Users, AlertTriangle, Coins, Zap, Info
} from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import api from "@/lib/api";
import useUiCopy from "@/hooks/useUiCopy";
import { fmtCurrency, scoreColor } from "@/lib/format";
import { isPaidTier } from "@/lib/tiers";
import { ScoreBadge } from "@/components/ScoreBadge";
import GeoHeatMap from "@/components/GeoHeatMap";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const copy = useUiCopy();
  const [data, setData] = useState(null);
  const [conv, setConv] = useState(null);
  const [s2o, setS2o] = useState(null);
  const [mfFeed, setMfFeed] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadS2o = useCallback(() => {
    api
      .get("/dashboard/score-to-outreach-rate")
      .then((r) => setS2o(r.data))
      .catch(() => setS2o({ rate: 0, got_outreach: 0, crossed_threshold: 0, blocked_no_contact: 0, blocked_manually: 0, period_days: 30, status: "no_data" }));
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/overview").then((r) => setData(r.data)).catch(() => setData(null)),
      api.get("/dashboard/outreach-conversion-rate").then((r) => setConv(r.data)).catch(() => setConv({ rate: 0, replied: 0, total_sent: 0, benchmark: 8.5, period_days: 30 })),
      api.get("/dashboard/score-to-outreach-rate").then((r) => setS2o(r.data)).catch(() => setS2o({ rate: 0, got_outreach: 0, crossed_threshold: 0, blocked_no_contact: 0, blocked_manually: 0, period_days: 30, status: "no_data" })),
      // BLOCK 51 Item 4 ,  Multifamily Deal Feed, strictly scoped to
      // scoring_stage=v2_full. Empty until the paid ATTOM plan runs
      // the Tier 2 lean batch against the 192,768-row multifamily pool.
      api.get("/top-picks?asset_type=multifamily&min_score=70&limit=8")
         .then((r) => setMfFeed(r.data))
         .catch(() => setMfFeed({ count: 0, items: [] })),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500">Loading dashboard…</div>;
  if (!data) return <div className="text-rose-400">Failed to load.</div>;

  const t = data.totals;

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Command Center</div>
          <h1 className="font-heading text-4xl tracking-tight">Acquisition Dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">Real-time intelligence across all opportunities.</p>
        </div>
        <Link to="/app/opportunities" data-testid="dashboard-view-all" className="btn-ghost text-sm inline-flex items-center gap-2">
          View all opportunities <ArrowUpRight className="h-4 w-4" />
        </Link>
      </header>

      {/* Hero ,  Outreach → Engaged Rate + Score → Outreach Rate (primary subscriber value metrics) */}
      <div className="grid md:grid-cols-2 gap-4">
        <OutreachConversionCard conv={conv} />
        <ScoreToOutreachCard s2o={s2o} onRefresh={loadS2o} />
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI testid="kpi-total" label="Total Opportunities" value={t.total} icon={TrendingUp} accent="emerald" />
        <KPI testid="kpi-pipeline-value" label="Pipeline Value" value={fmtCurrency(t.pipeline_value)} icon={DollarSign} accent="cyan" />
        <KPI testid="kpi-seller-financing" label="Seller Financing" value={t.seller_financing} icon={Coins} accent="amber" sub={`${t.creative_financing} creative`} />
        <KPI testid="kpi-distress" label="Distress Signals" value={t.distress} icon={AlertTriangle} accent="rose" />
      </div>

      {/* Asset breakdown */}
      <div className="grid md:grid-cols-3 gap-4">
        <AssetTile testid="asset-business" label="Businesses" value={t.business} icon={Building2} />
        <AssetTile testid="asset-multifamily" label="Multifamily" value={t.multifamily} icon={Building} />
        <AssetTile testid="asset-land" label="Land" value={t.land} icon={Mountain} />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Geographic distribution */}
        <div className="ae-card p-6 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Geographic Heat Map</div>
              <div className="font-heading text-lg mt-1">Opportunities by State</div>
            </div>
            <div className="text-xs text-slate-500">{data.by_state.length} states</div>
          </div>
          <GeoHeatMap data={data.by_state} />
        </div>

        {/* Quick stats */}
        <div className="ae-card p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Engagement</div>
          <Stat icon={Activity} label="In Pipeline" value={t.in_pipeline} testid="stat-in-pipeline" />
          <Stat icon={Users} label="Inbound Leads" value={t.leads} testid="stat-leads" />
          <Stat icon={Send} label="Outreach Sent" value={t.outreach_sent} testid="stat-outreach" />
          <Stat icon={Sparkles} label="Master Lease Ops" value={t.master_lease} testid="stat-master-lease" />
        </div>
      </div>

      {/* Bar chart */}
      <div className="ae-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Distribution</div>
            <div className="font-heading text-lg mt-1">Top States by Volume</div>
          </div>
          <div className="text-xs text-slate-500">Top 12</div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <BarChart data={data.by_state}>
              <XAxis dataKey="state" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <YAxis stroke="#64748B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <Tooltip
                cursor={{ fill: "rgba(16,185,129,0.06)" }}
                contentStyle={{
                  background: "#0E1116",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 4,
                  fontFamily: "IBM Plex Sans",
                  fontSize: 12,
                }}
              />
              <Bar dataKey="count" fill="#10B981" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top opportunities */}
      <div className="ae-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">AI Top Picks</div>
            <div className="font-heading text-lg mt-1">Highest Scoring Opportunities</div>
          </div>
        </div>
        <div className="divide-y divide-white/5">
          {data.top_opportunities.map((o) => (
            <Link
              key={o.oid}
              to={`/app/opportunities/${o.oid}`}
              data-testid={`top-opp-${o.oid}`}
              className="flex items-center gap-4 py-3 hover:bg-white/[0.03] transition-colors -mx-2 px-2 rounded-sm"
            >
              {/* BLOCK 33: authoritative score is the v2.1 cf_readiness_score,
                  matching Opportunities.jsx (Block 25). Legacy ai_score.opportunity_score
                  stays as a small secondary indicator only when populated. */}
              <div className="flex flex-col items-end w-14 shrink-0">
                <div
                  data-testid={`top-opp-cf-score-${o.oid}`}
                  className={`font-mono font-bold text-2xl tabular-nums leading-none ${scoreColor(o.cf_readiness_score || 0)}`}
                >
                  {o.cf_readiness_score ?? 0}
                </div>
                {(o.ai_score?.opportunity_score || 0) > 0 && (
                  <div
                    data-testid={`top-opp-ai-score-${o.oid}`}
                    className="font-mono text-[10px] tabular-nums text-slate-500 mt-0.5"
                    title="Legacy AI narrative score"
                  >
                    AI {o.ai_score.opportunity_score}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{o.title}</div>
                <div className="text-xs text-slate-500">
                  {o.city}, {o.state} · {o.asset_type} · {fmtCurrency(o.asking_price)}
                </div>
              </div>
              <div className="hidden md:flex gap-2">
                <ScoreBadge score={o.ai_score?.seller_financing_probability || 0} label="SF" size="sm" />
                <ScoreBadge score={o.ai_score?.creative_financing_probability || 0} label="CF" size="sm" />
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-500" />
            </Link>
          ))}
        </div>
      </div>

      {/* BLOCK 51 Item 4 ,  Multifamily Deal Feed (v2_full only). */}
      <div className="ae-card p-6" data-testid="mf-deal-feed-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Multifamily Deal Feed</div>
            <div className="font-heading text-lg mt-1">Fully-Enriched Multifamily · Top Picks</div>
            <div className="text-[11px] text-slate-500 mt-1">
              {mfFeed
                ? `${mfFeed.count} ${copy.dashboard_mf_scope}`
                : "loading…"}
            </div>
          </div>
          <Link
            to="/app/opportunities?asset_type=multifamily&min_score=70"
            data-testid="mf-deal-feed-view-all"
            className="btn-ghost text-xs inline-flex items-center gap-2"
          >
            All Multifamily <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {mfFeed && mfFeed.count > 0 ? (
          <div className="divide-y divide-white/5">
            {mfFeed.items.map((o) => (
              <Link
                key={o.oid}
                to={`/app/opportunities/${o.oid}`}
                data-testid={`mf-deal-feed-row-${o.oid}`}
                className="flex items-center gap-4 py-3 hover:bg-white/[0.03] transition-colors -mx-2 px-2 rounded-sm"
              >
                <div className="flex flex-col items-end w-14 shrink-0">
                  <div className={`font-mono font-bold text-2xl tabular-nums leading-none ${scoreColor(o.cf_readiness_score || 0)}`}>
                    {o.cf_readiness_score ?? 0}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{o.title || o.address}</div>
                  <div className="text-xs text-slate-500">
                    {o.city}, {o.state}
                    {typeof o.attom_ltv === "number" && (
                      <> · LTV {o.attom_ltv.toFixed(0)}%</>
                    )}
                    {typeof o.attom_lendable_equity === "number" && o.attom_lendable_equity > 0 && (
                      <> · lendable ${Math.round(o.attom_lendable_equity / 1000)}k</>
                    )}
                    {" · "}{fmtCurrency(o.asking_price || o.just_value)}
                  </div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-500" />
              </Link>
            ))}
          </div>
        ) : (
          <div
            className="text-xs text-slate-500 border border-dashed border-slate-700 rounded-sm p-4 text-center"
            data-testid="mf-deal-feed-empty"
          >
            {mfFeed?.pool_progress ? (
              <>
                <div className="text-slate-300 mb-1" data-testid="mf-deal-feed-progress">
                  <span className="font-mono text-emerald-400 font-semibold" data-testid="mf-progress-graduated">
                    {mfFeed.pool_progress.graduated_v2_full.toLocaleString()}
                  </span>
                  {" of "}
                  <span className="font-mono text-slate-200 font-semibold" data-testid="mf-progress-total">
                    {mfFeed.pool_progress.total_pool.toLocaleString()}
                  </span>
                  {" multifamily parcels graduated to "}
                  <span className="text-emerald-400">v2_full</span>
                  {" ("}
                  <span data-testid="mf-progress-pct">{mfFeed.pool_progress.graduated_pct}</span>
                  {"%)."}
                </div>
                <div>
                  The remaining{" "}
                  <span className="font-mono text-amber-400" data-testid="mf-progress-remaining">
                    {mfFeed.pool_progress.remaining_first_pass.toLocaleString()}
                  </span>{" "}
                  {copy.dashboard_mf_progress}
                </div>
              </>
            ) : (
              <>{copy.dashboard_mf_empty}</>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, icon: I, accent, sub, testid }) {
  const accentMap = {
    emerald: "text-emerald-400 bg-emerald-500/10",
    cyan: "text-cyan-400 bg-cyan-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    rose: "text-rose-400 bg-rose-500/10",
  };
  return (
    <div className="ae-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className={`h-7 w-7 rounded-sm flex items-center justify-center ${accentMap[accent]}`}>
          <I className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="font-mono tabular-nums text-3xl font-bold mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function OutreachConversionCard({ conv }) {
  const rate = conv?.rate ?? 0;
  const replied = conv?.replied ?? 0;
  const totalSent = conv?.total_sent ?? 0;
  const benchmark = conv?.benchmark ?? 8.5;

  const tone = rate > benchmark
    ? { text: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: "text-emerald-400" }
    : rate >= 3
    ? { text: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: "text-amber-400" }
    : { text: "text-slate-300", bg: "bg-slate-500/10", border: "border-slate-500/30", icon: "text-slate-400" };

  const emptyState = totalSent === 0
    ? "No auto-outreach sent yet. Leads scoring 70+ trigger sequences automatically."
    : replied === 0
    ? "First replies typically arrive 3-5 days after sequences start."
    : null;

  const tooltip = "Percentage of auto-fired email sequences that received a reply within 7 days. Sequences fire automatically when a lead scores 70 or above.";

  return (
    <div
      data-testid="outreach-conversion-card"
      title={tooltip}
      className={`ae-card p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 border ${tone.border} ${tone.bg}`}
    >
      <div className={`h-14 w-14 rounded-md flex items-center justify-center ${tone.bg} border ${tone.border}`}>
        <Zap className={`h-7 w-7 ${tone.icon}`} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400">
          Outreach → Engaged Rate
          <Info className="h-3 w-3 text-slate-500 cursor-help" />
        </div>
        <div className="text-xs text-slate-500 mt-1">Auto-fired sequences that got a reply within 7 days</div>
        <div className="flex items-baseline gap-3 mt-2">
          <div
            data-testid="outreach-conversion-rate-value"
            className={`font-mono tabular-nums text-5xl font-bold ${tone.text}`}
          >
            {rate}%
          </div>
          {totalSent > 0 && (
            <div className="text-xs text-slate-400">
              {replied} {replied === 1 ? "reply" : "replies"} from {totalSent} {totalSent === 1 ? "sequence" : "sequences"} (last {conv?.period_days ?? 30} days)
            </div>
          )}
        </div>
        {emptyState ? (
          <div data-testid="outreach-conversion-empty" className="text-sm text-slate-400 mt-2">{emptyState}</div>
        ) : (
          <div data-testid="outreach-conversion-benchmark" className="text-xs text-slate-400 mt-2">
            Industry avg: <span className="font-mono">{benchmark}%</span> ,  You&apos;re at <span className={`font-mono font-semibold ${tone.text}`}>{rate}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ScoreToOutreachCard({ s2o, onRefresh }) {
  const [enriching, setEnriching] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const { user } = useAuth();
  const isPaid = isPaidTier(user?.tier) || ["admin", "super_admin"].includes(user?.role || "");
  const cooldownActive = Date.now() < cooldownUntil;
  const cooldownRemaining = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 60000));

  const rate = s2o?.rate ?? 0;
  const got = s2o?.got_outreach ?? 0;
  const crossed = s2o?.crossed_threshold ?? 0;
  const blockedNoContact = s2o?.blocked_no_contact ?? 0;
  const blockedManually = s2o?.blocked_manually ?? 0;
  const blockedNoSenderProfile = s2o?.blocked_no_sender_profile ?? 0;
  const senderProfileReady = s2o?.sender_profile_ready !== false;
  const noData = (s2o?.status === "no_data") || crossed === 0;

  const enrichBlocked = async () => {
    setEnriching(true);
    try {
      const r = await api.post("/opportunities/enrich-blocked-hot-leads");
      toast.success(r.data?.message || `Enriching ${r.data?.triggered ?? 0} blocked leads. Contact data will appear within 2 minutes.`);
      const cooldownSec = r.data?.cooldown_seconds ?? 300;
      setCooldownUntil(Date.now() + cooldownSec * 1000);
      if (onRefresh) setTimeout(onRefresh, 3000);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        // Server-enforced cooldown ,  sync the timer to the server window.
        // Clamp minimum to 1s so we always show a lock state instead of
        // instantly re-arming when the server reports 0 remaining.
        const raw = (typeof detail === "object" ? detail?.retry_after_seconds : 0) || 0;
        const retryAfter = Math.max(1, raw);
        setCooldownUntil(Date.now() + retryAfter * 1000);
        toast.warning(
          (typeof detail === "object" ? detail?.message : null) ||
            `Enrichment already running. Try again in ${retryAfter}s.`
        );
      } else if (status === 402) {
        toast.error("Upgrade to Pro to enrich blocked leads.");
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Enrichment failed");
      }
    } finally {
      setEnriching(false);
    }
  };

  const tone = noData
    ? { text: "text-slate-300", bg: "bg-slate-500/10", border: "border-slate-500/30", icon: "text-slate-400" }
    : rate > 80
    ? { text: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: "text-emerald-400" }
    : rate >= 50
    ? { text: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: "text-amber-400" }
    : { text: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30", icon: "text-rose-400" };

  const tooltip = "Of all leads that scored 70 or above in the last 30 days, the percentage that landed on your Ready-to-Contact review queue (they didn't auto-fire ,  you approve each one). The sub-metric shows how many of those you have actually approved and contacted.";

  return (
    <div
      data-testid="score-to-outreach-card"
      title={tooltip}
      className={`ae-card p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 border ${tone.border} ${tone.bg}`}
    >
      <div className={`h-14 w-14 rounded-md flex items-center justify-center ${tone.bg} border ${tone.border}`}>
        <TrendingUp className={`h-7 w-7 ${tone.icon}`} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400">
          Qualification → Review Queue
          <Info className="h-3 w-3 text-slate-500 cursor-help" />
        </div>
        <div className="text-xs text-slate-500 mt-1">Hot leads that landed on your Ready-to-Contact queue</div>
        <div className="flex items-baseline gap-3 mt-2">
          <div
            data-testid="score-to-outreach-rate-value"
            className={`font-mono tabular-nums text-5xl font-bold ${tone.text}`}
          >
            {noData ? ", " : `${rate}%`}
          </div>
          {!noData && (
            <div className="text-xs text-slate-400">
              {(s2o?.reached_queue ?? got)} of {crossed} hot {crossed === 1 ? "lead" : "leads"} queued · last {s2o?.period_days ?? 30} days
            </div>
          )}
        </div>
        {!noData && (
          <div
            data-testid="score-to-outreach-approved-submetric"
            className="text-xs text-slate-400 mt-1"
          >
            <span className="text-emerald-300 font-semibold">{s2o?.approved_and_contacted ?? got}</span>
            &nbsp;of&nbsp;{s2o?.reached_queue ?? got}
            &nbsp;approved &amp; contacted
            &nbsp;({s2o?.rate_approved ?? 0}% of qualifying leads).
            {(s2o?.reached_queue ?? 0) > (s2o?.approved_and_contacted ?? 0) && (
              <>
                &nbsp;<Link to="/app/outreach" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">
                  {(s2o?.reached_queue ?? 0) - (s2o?.approved_and_contacted ?? 0)} waiting for your review →
                </Link>
              </>
            )}
          </div>
        )}
        {noData ? (
          <div data-testid="score-to-outreach-empty" className="text-sm text-slate-400 mt-2">
            Leads scoring 70+ will land on your Ready-to-Contact queue for your approval.
          </div>
        ) : (
          <div className="space-y-1 mt-2">
            {blockedNoContact > 0 && (
              <>
                <div data-testid="score-to-outreach-blocked-no-contact" className="text-xs text-amber-300">
                  {blockedNoContact} {blockedNoContact === 1 ? "lead had" : "leads had"} no contact info ,  enrich to unlock outreach
                </div>
                {isPaid ? (
                  <button
                    onClick={enrichBlocked}
                    disabled={enriching || cooldownActive}
                    data-testid="enrich-blocked-btn"
                    className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-amber-500/50 bg-amber-500/15 text-amber-200 text-xs hover:bg-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={cooldownActive ? `Cooldown ,  try again in ${cooldownRemaining}m` : undefined}
                  >
                    <Zap className={`h-3 w-3 ${enriching ? "animate-pulse" : ""}`} />
                    {enriching
                      ? "Enriching…"
                      : cooldownActive
                      ? `Cooldown ${cooldownRemaining}m`
                      : `Enrich ${blockedNoContact} Blocked ${blockedNoContact === 1 ? "Lead" : "Leads"} Now`}
                  </button>
                ) : (
                  <button
                    disabled
                    data-testid="enrich-blocked-btn-locked"
                    title="Upgrade to Pro to enrich blocked leads."
                    className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-slate-600/50 bg-slate-700/20 text-slate-500 text-xs cursor-not-allowed"
                  >
                    <Zap className="h-3 w-3" />
                    Upgrade to enrich blocked leads
                  </button>
                )}
              </>
            )}
            {blockedManually > 0 && (
              <div data-testid="score-to-outreach-blocked-manually" className="text-xs text-slate-400">
                {blockedManually} {blockedManually === 1 ? "lead" : "leads"} paused manually
              </div>
            )}
            {!senderProfileReady && (
              <div data-testid="score-to-outreach-blocked-no-sender-profile" className="text-xs text-amber-300 flex items-center gap-2 mt-1.5">
                <span>Complete your sender profile to activate outreach{blockedNoSenderProfile > 0 ? ` (${blockedNoSenderProfile} ${blockedNoSenderProfile === 1 ? "lead" : "leads"} waiting)` : ""}.</span>
                <Link to="/app/settings" data-testid="s2o-open-settings" className="underline text-amber-200 hover:text-amber-100">
                  Open Settings
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function AssetTile({ label, value, icon: I, testid }) {
  return (
    <div className="ae-card p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-sm bg-white/5 flex items-center justify-center">
          <I className="h-5 w-5 text-emerald-400" strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
          <div className="font-mono tabular-nums text-2xl font-bold">{value}</div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: I, label, value, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <div className="flex items-center gap-2 text-sm text-slate-300">
        <I className="h-3.5 w-3.5 text-emerald-400" />
        {label}
      </div>
      <div className="font-mono tabular-nums font-semibold">{value}</div>
    </div>
  );
}
